/**
 * Replica of AfterSell's free-shipping bar, for Intelligems shipping-threshold tests.
 *
 * Intelligems' own `<ig-progress-bar>` cannot render inside AfterSell's shadow root, and
 * AfterSell's threshold is per-market app config with no per-visitor API, so neither can follow a
 * test. This paints a copy into AfterSell's custom-code block and hides the original — hiding it
 * from the same injected stylesheet, so the two are strictly coupled and a shopper always sees
 * exactly one bar.
 *
 * Standalone rather than part of the `src/` bundle because it is scoped to a temporary experiment:
 * one tag in `layout/theme.liquid`, deleted along with this file when the test ends. Loaded as a
 * module so these declarations stay off the global scope.
 *
 * The threshold comes from `igData.campaigns.getAll()` — the offers attached to this visitor's own
 * assigned variation — as `discount.tiers[].minimumUnits`. That is the same config Intelligems
 * applies the shipping rate from, so the bar and checkout cannot disagree. It is also why there is
 * no experiment id, group id or threshold here: the control arm gets no campaign and never renders,
 * and an untargeted market never sees the test at all.
 *
 * Testing: Intelligems is gated on `theme.role == 'main'`, so an unpublished theme has no
 * `igData`; `?ig_shipping_bar=35` stands in for a campaign there, `?ig_shipping_bar=off` clears it.
 * To pin a production session to an arm, add `igTg=<test group id>` to a preview URL.
 */

const MOUNT_ID = 'intelligems-shipping-bar';
const BLOCK = 'cart-shipping-progress';
const STYLE_FLAG = 'data-cart-shipping-progress';

// The element carries both class names; matching both survives either being dropped.
const NATIVE_BAR_SELECTOR = '.cart-external-rewards, .cart-internal-rewards';

const CART_UPDATED_EVENT = 'aftersell:cart:cart-updated';
const IG_READY_EVENT = 'ig:ready';
const IG_SCRIPT_SELECTOR = 'script[src*="cdn.intelligems.io"]';
const ROOT_POLL_INTERVAL = 250;
const ROOT_POLL_TIMEOUT = 30000;

const IG_SETTLE_TIMEOUT = 10000;

// Shopify's convention, not the currency's ISO minor units: /cart.js treats every currency as
// hundredths, zero-decimal ones included. Deriving this per currency would turn ¥5,000 into ¥50.
const CENTS_PER_UNIT = 100;
const THRESHOLD_PATTERN = /^\d{1,7}(\.\d{1,2})?$/;
const MIN_THRESHOLD_CENTS = 100;
const MAX_THRESHOLD_CENTS = 10000000;

const OVERRIDE_PARAM = 'ig_shipping_bar';
const OVERRIDE_KEY = 'fahlo:ig-shipping-bar';

// Copy and colours transcribed from AfterSell's rewards config (US binding), so the two arms read
// identically apart from the number.
const INSTANCE_STYLE = [
  '--aftersell-cart-rewards-message-align: left',
  '--aftersell-cart-rewards-label-align: center',
  '--aftersell-cart-rewards-fill: #F47575',
  '--aftersell-cart-rewards-track: #E2E2E2',
  '--aftersell-cart-rewards-icon-filled: #FFF8F1',
  '--aftersell-cart-rewards-icon-bg-filled: #F47575',
  '--aftersell-cart-rewards-icon-radius: 26px',
].join(';');

const AWAY_MESSAGE = `You're <strong>{{amount}}</strong> away from free shipping!`;
const UNLOCKED_MESSAGE = `<span style="color: rgb(48, 48, 48)"><strong>Free shipping unlocked!</strong></span>`;

const ICON_PATH = `M4.75 4.5a.75.75 0 0 0 0 1.5h3.25a1 1 0 0 1 0 2h-4.75a.75.75 0 0 0 0 1.5h3a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 0 0 1.5h.458a2.5 2.5 0 1 0 4.78.75h3.024a2.5 2.5 0 1 0 4.955-.153 1.75 1.75 0 0 0 1.033-1.597v-1.22a1.75 1.75 0 0 0-1.326-1.697l-1.682-.42a.25.25 0 0 1-.18-.174l-.426-1.494a2.75 2.75 0 0 0-2.645-1.995h-6.991Zm2.75 9a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm8 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z`;

const ICON_SVG = `<svg class="${BLOCK}__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false"><path fill-rule="evenodd" d="${ICON_PATH}"></path></svg>`;

// AfterSell's rewards rules with the block renamed. The `--aftersell-cart-*` properties are kept
// so the replica inherits the drawer's theme. No box-sizing reset — theirs already covers us.
const STYLES = `
  .${BLOCK} {
    padding-top: var(--aftersell-cart-rewards-padding-block, 16px);
    padding-bottom: var(--aftersell-cart-rewards-padding-block, 20px);
    padding-inline: var(--aftersell-cart-rewards-padding-inline, var(--aftersell-cart-padding-horizontal, 24px));
    margin-inline: var(--aftersell-cart-rewards-margin-inline, 0);
    margin-block: var(--aftersell-cart-rewards-margin-block, 0);
    background: var(--aftersell-cart-rewards-background, transparent);
  }
  .${BLOCK}__message {
    margin: 0 0 12px;
    font-family: var(--aftersell-cart-rewards-message-font-family, inherit);
    font-size: var(--aftersell-cart-rewards-message-font-size, 14px);
    line-height: var(--aftersell-cart-rewards-message-line-height, 1.43);
    letter-spacing: var(--aftersell-cart-rewards-message-tracking, normal);
    color: var(--aftersell-cart-text, #000000);
    text-align: var(--aftersell-cart-rewards-message-align, left);
  }
  .${BLOCK}__bar {
    display: flex;
  }
  .${BLOCK}__segment {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: flex-start;
  }
  .${BLOCK}__column {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .${BLOCK}__track {
    height: var(--aftersell-cart-rewards-track-height, 5px);
    border-radius: var(--aftersell-cart-rewards-track-radius, 999px);
    background: var(--aftersell-cart-rewards-track, #e9e9e9);
    overflow: hidden;
  }
  .${BLOCK}__fill {
    height: 100%;
    border-radius: inherit;
    background: var(--aftersell-cart-rewards-fill, #000000);
    transition: width 0.25s ease;
  }
  .${BLOCK}__icon-container {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--aftersell-cart-rewards-icon-padding, 5px);
    border-radius: var(--aftersell-cart-rewards-icon-radius, 0);
    background: var(--aftersell-cart-rewards-icon-bg-unfilled, transparent);
    transform: translateY(calc(var(--aftersell-cart-rewards-track-height, 5px) / 2 - 50%));
  }
  .${BLOCK}__segment[data-tier-reached='true'] .${BLOCK}__icon-container {
    background: var(--aftersell-cart-rewards-icon-bg-filled, transparent);
  }
  .${BLOCK}__icon {
    flex: none;
    width: var(--aftersell-cart-rewards-icon-size, 20px);
    height: var(--aftersell-cart-rewards-icon-size, 20px);
    color: var(--aftersell-cart-rewards-icon-unfilled, var(--aftersell-cart-text-secondary, #a8a8a8));
  }
  .${BLOCK}__segment[data-tier-reached='true'] .${BLOCK}__icon {
    color: var(--aftersell-cart-rewards-icon-filled, var(--aftersell-cart-text, #000000));
  }
  @media (prefers-reduced-motion: reduce) {
    .${BLOCK}__fill {
      transition: none;
    }
  }
  ${NATIVE_BAR_SELECTOR} {
    display: none !important;
  }
`;

// ---------------------------------------------------------------------------
// Market

/** AfterSell's inline page context — the only in-page source of the Shopify market handle. */
const readAfterSellContext = () => {
  const cart = window.aftersell && window.aftersell.cart;

  return (cart && cart.context) || null;
};

// ---------------------------------------------------------------------------
// Threshold

/**
 * Parses a main-currency-unit amount into cents.
 *
 * String arithmetic rather than `value * 100`: the pattern doubles as the whitelist, and the split
 * is exact for everything it admits.
 */
const toCents = (value) => {
  const text = String(value).trim();

  if (!THRESHOLD_PATTERN.test(text)) {
    return null;
  }

  const [whole, fraction = ''] = text.split('.');
  const cents = (Number(whole) * CENTS_PER_UNIT) + Number(fraction.padEnd(2, '0'));

  return cents >= MIN_THRESHOLD_CENTS && cents <= MAX_THRESHOLD_CENTS ? cents : null;
};

const isIntelligemsReady = () =>
  Boolean(
    window.igData && window.igData.campaigns && typeof window.igData.campaigns.getAll === 'function'
  );

/**
 * The free-shipping threshold Intelligems is applying to this visitor, or null to stand down.
 *
 * `getAll()` returns only the offers attached to the visitor's assigned variation, so this is
 * arm-correct by construction: the control arm comes back empty and never renders.
 *
 * Not gated on `discount.enabled` — that reads `false` on a live campaign that is applying its
 * rate, so it means something other than "active".
 */
const readCampaignThresholdCents = () => {
  let campaigns = [];

  try {
    campaigns = window.igData.campaigns.getAll() || [];
  } catch (error) {
    return null;
  }

  const thresholds = campaigns
    .map((campaign) => campaign && campaign.discount)
    .filter(
      (discount) =>
        discount &&
        discount.discountApplicationType === 'freeShipping' &&
        // A currency amount rather than an item count, which must not be read as money.
        discount.unitType === 'dollar' &&
        !discount.isArchived &&
        Array.isArray(discount.tiers)
    )
    .reduce((found, discount) => found.concat(discount.tiers), [])
    .filter((tier) => tier && tier.isFreeShipping)
    .map((tier) => toCents(tier.minimumUnits))
    .filter((cents) => cents !== null);

  // One segment, so across several tiers the one being worked towards is the cheapest.
  return thresholds.length ? Math.min(...thresholds) : null;
};

/**
 * Runs the callback once Intelligems has had its say, or once it is clear it never will.
 *
 * The already-ready check is not optional: this script is deferred and the Intelligems tag is
 * `async blocking="render"`, so `ig:ready` may well have fired first. `window.onIgReady` would be
 * the documented alternative but clobbers other consumers. With no Intelligems tag on the page the
 * timeout is skipped, so the dev override works immediately on an unpublished theme.
 */
function whenIntelligemsSettled(callback) {
  if (isIntelligemsReady() || !document.querySelector(IG_SCRIPT_SELECTOR)) {
    callback();
    return;
  }

  let settled = false;
  let timer = null;
  const listener = new AbortController();

  const finish = () => {
    if (settled) {
      return;
    }
    settled = true;
    listener.abort();
    window.clearTimeout(timer);
    callback();
  };

  timer = window.setTimeout(finish, IG_SETTLE_TIMEOUT);
  window.addEventListener(IG_READY_EVENT, () => finish(), {
    once: true,
    signal: listener.signal,
  });
}

// ---------------------------------------------------------------------------
// Dev override
//
// Gated on Intelligems being absent rather than on the theme role, which makes it structurally
// unreachable in production.

const readSessionValue = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const writeSessionValue = (key, value) => {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, value);
    }
  } catch (error) {
    // Private browsing. Losing a dev affordance is not worth a throw.
  }
};

/** Reads `?ig_shipping_bar=<amount>`, persisted so it survives navigation during a test run. */
const readOverrideThresholdCents = () => {
  const requested = new URLSearchParams(window.location.search).get(OVERRIDE_PARAM);

  if (requested === 'off') {
    writeSessionValue(OVERRIDE_KEY, null);
    return null;
  }

  if (requested) {
    writeSessionValue(OVERRIDE_KEY, requested);
  }

  const stored = requested || readSessionValue(OVERRIDE_KEY);

  return stored ? toCents(stored) : null;
};

/** The threshold to paint, or null to leave the cart alone. */
const resolveThresholdCents = () => {
  if (isIntelligemsReady()) {
    return readCampaignThresholdCents();
  }

  // Only reachable where Intelligems never loads, so it cannot affect a real shopper.
  return readOverrideThresholdCents();
};

// ---------------------------------------------------------------------------
// Cart
//
// Duplicated from src/js/aftersell-cart-observer.js, the canonical seam, which carries the full
// reasoning for reading /cart.js rather than the SDK's cart object and for listening on `window`
// rather than the SDK bus. A standalone script cannot import it.

const readCart = () => window.fetch('/cart.js').then((response) => response.json());

/** @returns {Function} Teardown that removes the listener. */
const onCartChange = (handler) => {
  let requestId = 0;

  const read = () => {
    // Two events can land in quick succession, each starting its own read, so a slow earlier
    // response must not overwrite a faster later one.
    const myId = ++requestId;

    readCart()
      .then((cart) => {
        if (myId === requestId) {
          handler(cart);
        }
        return null;
      })
      .catch((error) => {
        console.error('Error fetching cart data:', error);
      });
  };

  window.addEventListener(CART_UPDATED_EVENT, read);

  return () => {
    window.removeEventListener(CART_UPDATED_EVENT, read);
  };
};

/** The AfterSell cart's shadow root, which boots well after this script runs. */
const waitForCartRoot = () =>
  new Promise((resolve) => {
    const deadline = Date.now() + ROOT_POLL_TIMEOUT;

    const poll = () => {
      const root = window.aftersell && window.aftersell.cart && window.aftersell.cart.shadowRoot;

      if (root) {
        resolve(root);
      } else if (Date.now() > deadline) {
        resolve(null);
      } else {
        window.setTimeout(poll, ROOT_POLL_INTERVAL);
      }
    };

    poll();
  });

// ---------------------------------------------------------------------------
// Rendering

const buildBar = () => {
  const root = document.createElement('div');
  root.className = BLOCK;
  root.setAttribute('style', INSTANCE_STYLE);

  const message = document.createElement('p');
  message.className = `${BLOCK}__message`;

  const bar = document.createElement('div');
  bar.className = `${BLOCK}__bar`;

  const segment = document.createElement('div');
  segment.className = `${BLOCK}__segment`;
  segment.setAttribute('data-tier-reached', 'false');

  const column = document.createElement('div');
  column.className = `${BLOCK}__column`;

  const track = document.createElement('div');
  track.className = `${BLOCK}__track`;

  const fill = document.createElement('div');
  fill.className = `${BLOCK}__fill`;

  const iconContainer = document.createElement('span');
  iconContainer.className = `${BLOCK}__icon-container`;
  iconContainer.innerHTML = ICON_SVG;

  track.appendChild(fill);
  column.appendChild(track);
  segment.appendChild(column);
  segment.appendChild(iconContainer);
  bar.appendChild(segment);
  root.appendChild(message);
  root.appendChild(bar);

  return {root, message, segment, fill};
};

/**
 * AfterSell's own formatter, so amounts match their bar in every market without re-deriving each
 * market's money format.
 *
 * The fallback pins `'en'` rather than the visitor's locale: money format follows the Shopify
 * market, not the browser, so a German-locale visitor in the US market must not see `35,00 $`.
 */
const formatMoney = (cents, currency) => {
  const actions = window.aftersell && window.aftersell.cart && window.aftersell.cart.actions;

  if (actions && typeof actions.formatMoney === 'function') {
    try {
      const formatted = actions.formatMoney(cents);

      if (typeof formatted === 'string' && formatted) {
        return formatted;
      }
    } catch (error) {
      // Fall through to Intl.
    }
  }

  return new Intl.NumberFormat('en', {style: 'currency', currency}).format(cents / CENTS_PER_UNIT);
};

/**
 * AfterSell nests an inner centred `<p>` inside its message, which survives because it is written
 * through innerHTML rather than parsed as page markup. Reproducing the nesting keeps the inherited
 * alignment and default paragraph margins identical — flattening it shifts the bar by 2px.
 */
const renderMessage = (message, html) => {
  message.innerHTML = `<p style="text-align: center">${html}</p>`;
};

const paint = (parts, cart, thresholdCents) => {
  // AfterSell's tier uses `basis: "cartTotal"` and Shopify evaluates shipping rates against the
  // post-discount subtotal. PWYW applies a cart-level code, so items_subtotal_price would overstate.
  const subtotalCents = cart.total_price;
  const reached = subtotalCents >= thresholdCents;
  const progress = Math.min(1, subtotalCents / thresholdCents);

  // Unrounded, matching the native bar: at the same cart value the two must agree exactly.
  parts.fill.style.width = `${progress * 100}%`;
  parts.segment.setAttribute('data-tier-reached', String(reached));
  // Tells the replica apart from AfterSell's visually identical bar during QA.
  parts.root.setAttribute('data-threshold-cents', String(thresholdCents));

  let message = UNLOCKED_MESSAGE;

  if (!reached) {
    const amount = formatMoney(thresholdCents - subtotalCents, cart.currency);

    // Function replacement: `$&`, `` $` `` and `$'` are patterns in a replacement string, and the
    // amount is money.
    message = AWAY_MESSAGE.replace('{{amount}}', () => amount);
  }

  renderMessage(parts.message, message);
};

/**
 * Injects the replica's stylesheet, which also carries the rule hiding AfterSell's own bar.
 *
 * Done as soon as the shadow root exists rather than at mount time: waiting for the first /cart.js
 * read would flash the untested threshold at a shopper who is in the test.
 */
const ensureStyle = (root) => {
  if (root.querySelector(`style[${STYLE_FLAG}]`)) {
    return;
  }

  const style = document.createElement('style');
  style.setAttribute(STYLE_FLAG, '');
  style.textContent = STYLES;

  // Appended to the shadow root itself, a sibling of AfterSell's <dialog> and of its stylesheet
  // <link>, so an inner re-render cannot remove it and it still wins ties by source order.
  root.appendChild(style);
};

/** Puts AfterSell's own bar back, for the paths where the replica cannot be shown. */
const removeStyle = (root) => {
  const style = root.querySelector(`style[${STYLE_FLAG}]`);

  if (style) {
    style.remove();
  }
};

// ---------------------------------------------------------------------------

function start(thresholdCents, root, context) {
  const parts = buildBar();
  let cart = null;
  // Distinct from `cart === null`, which only means the first read has not landed yet. Standing
  // down has to restore AfterSell's bar; waiting must not.
  let standDown = false;
  let observer = null;
  let unsubscribe = null;

  const detach = () => {
    if (parts.root.parentNode) {
      parts.root.parentNode.removeChild(parts.root);
    }
  };

  /**
   * Idempotent, which is the whole defence against the observer feeding itself: every exit above
   * the final append leaves the DOM untouched, so our own mutations cost one no-op pass.
   */
  const reconcile = () => {
    if (standDown) {
      detach();
      removeStyle(root);
      // Terminal: currency cannot change without a reload, so nothing can bring the bar back.
      if (observer) {
        observer.disconnect();
      }
      if (unsubscribe) {
        unsubscribe();
      }
      return;
    }

    // The stylesheet stays put while waiting, so the native bar is already suppressed.
    if (!cart) {
      return;
    }

    const host = root.getElementById(MOUNT_ID);

    // The empty-cart view has no custom-code block at all.
    if (!host) {
      return;
    }

    ensureStyle(root);

    if (host.contains(parts.root)) {
      return;
    }

    host.appendChild(parts.root);
  };

  let scheduled = false;

  const schedule = () => {
    if (scheduled) {
      return;
    }
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      reconcile();
    });
  };

  const apply = (nextCart) => {
    // A disagreement means the page context is stale — cached HTML, or a currency switched without
    // a reload — so hand the cart back rather than measure one currency against another.
    standDown = Boolean(context) && nextCart.currency !== context.customer_currency;

    if (standDown) {
      reconcile();
      return;
    }

    cart = nextCart;
    // Painting before mounting means a re-injection after a wipe never shows an unpainted frame.
    paint(parts, cart, thresholdCents);
    reconcile();
  };

  // Before the cart read, so the tested arm never sees the untested threshold.
  ensureStyle(root);

  // AfterSell re-renders the cart body and publishes no re-render event, so the observer is the
  // only way to notice the custom-code block being wiped.
  observer = new MutationObserver(schedule);

  observer.observe(root, {
    childList: true,
    subtree: true,
  });

  unsubscribe = onCartChange(apply);

  readCart()
    .then((initialCart) => {
      apply(initialCart);
      return null;
    })
    .catch((error) => {
      // No cart means no bar, so give AfterSell's back rather than leaving the drawer with none.
      standDown = true;
      reconcile();
      console.error('Error fetching cart data:', error);
    });
}

// Started before Intelligems settles: the two are independent, and holding the root early is what
// keeps AfterSell's bar from flashing.
const rootPromise = waitForCartRoot();

whenIntelligemsSettled(() => {
  const context = readAfterSellContext();
  const thresholdCents = resolveThresholdCents();

  // The control arm, untargeted markets and everyone outside the test stop here, costing one
  // campaigns read: no cart fetch, no observer, no listener.
  if (!thresholdCents) {
    return;
  }

  rootPromise
    .then((root) => {
      if (root) {
        start(thresholdCents, root, context);
      }
      return null;
    })
    .catch(() => null);
});
